import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  Scale, 
  EventBusy, 
  Icecream, 
  QrCodeScanner, 
  LocalOffer 
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import PublicLayout from "../../layouts/PublicLayout";

export default function IceCream() {
  return (
    <PublicLayout>
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: '#f6521a', fontWeight: 800, letterSpacing: 1.5 }}>
                TPOS FOR ICE CREAM PARLORS & DESSERT BARS
              </Typography>
              
              <Typography variant="h2" sx={{ fontWeight: 600, mt: 1, mb: 2, fontSize: { xs: '1.5rem', md: '2.5rem' }, lineHeight: 1.1 }}>
                Sweeten Your Profits with <span style={{ color: "#f6521a" }}>Precision Weighing and Tracking.</span>
              </Typography>

              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.5 }}>
                Manage high-frequency micro-transactions, seasonal flavor rotations, and cold-chain inventory with absolute accuracy.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.primary', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                Ice cream parlors and dessert bars deal with unique operational hurdles: high foot traffic, small ticket sizes, and perishable inventory that melts or expires. 
                Whether you sell by the scoop, by exact weight, or in pre-packaged tubs, you need a POS that is incredibly fast and highly precise. 
                <strong> TPOS integrates directly with digital weighing scales and tracks batch-wise expiry dates</strong>, ensuring your business never loses money to shrinkage or unauthorized over-scooping.
              </Typography>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Specialized TPOS Features:
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    title: "Direct Weighing Scale Integration",
                    desc: "Connect your digital scale via Bluetooth/USB. The POS automatically fetches the exact weight and calculates the price instantly.",
                    icon: <Scale sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Batch & Expiry Management", 
                    desc: "Track raw materials by specific batch numbers. Receive automated alerts when dairy products are nearing their expiration.",
                    icon: <EventBusy sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Dynamic Flavor Matrix", 
                    desc: "Add complex modifiers like 'Waffle Cone', 'Extra Sprinkles', or 'Half-and-Half' without cluttering your menu screen.",
                    icon: <Icecream sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Quick-Scan Barcode Billing", 
                    desc: "Instantly bill pre-packaged items like popsicles, family tubs, or bottled beverages using a standard barcode scanner.",
                    icon: <QrCodeScanner sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Smart Combo Management", 
                    desc: "Easily set up rules for 'Buy 2 Scoops, Get 1 Free' or Family Pack discounts that apply automatically at checkout.",
                    icon: <LocalOffer sx={{ color: '#f6521a' }} />
                  }
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={5} sx={{ position: 'sticky', top: 100 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden', 
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 550 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'right',
                    float:'right'                    
                  }}
                >
                  <img 
                    src="/img/icecreams.png" 
                    alt="Ice Cream Parlor POS" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                
                <Box sx={{ p: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Business Impact</Typography>
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Minimize inventory shrinkage by up to 25% through precise ingredient-level tracking." />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Reduce checkout times by 40% during peak evening and weekend rushes." />
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </PublicLayout>
  );
}